import React, { useState, useMemo } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Selector con búsqueda en vivo de PLANES (para el Macro Builder).
 * Filtra por nombre + objetivo (insensible a tildes/mayúsculas).
 *
 * Props:
 *   - plans: [{ id, name, objective?, weeks? }, ...]
 *   - value: string (id del plan seleccionado)
 *   - onChange: (id, plan) => void
 *   - placeholder, testId, className
 */
const norm = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const PlanSearchSelect = ({
  plans = [],
  value,
  onChange,
  placeholder = 'Buscar plan...',
  className = '',
  testId = 'plan-search-select',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = useMemo(() => plans.find((p) => p.id === value) || null, [plans, value]);

  const filtered = useMemo(() => {
    const q = norm(query);
    if (!q) return plans;
    const tokens = q.split(/\s+/).filter(Boolean);
    return plans.filter((p) => {
      const hay = norm([p.name, p.objective].join(' '));
      return tokens.every((t) => hay.includes(t));
    });
  }, [plans, query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={`w-full justify-between bg-zinc-900/50 border-amber-500/30 h-10 text-left font-normal ${className}`}
        >
          <span className={selected ? 'text-foreground truncate' : 'text-muted-foreground'}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 bg-zinc-900 border-zinc-700"
        align="start"
      >
        <div className="flex items-center border-b border-zinc-800 px-2 focus-within:ring-amber-500/50">
          <Search className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Escribe: senior, fase 2, atlético..."
            className="h-9 border-0 bg-transparent focus-visible:ring-0 px-0"
            data-testid={`${testId}-input`}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Limpiar"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-6 px-3">
              Sin resultados para "{query}"
            </div>
          ) : (
            filtered.map((p) => {
              const isSel = p.id === value;
              return (
                <button
                  type="button"
                  key={p.id}
                  data-testid={`${testId}-opt-${p.id}`}
                  onClick={() => {
                    onChange(p.id, p);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-amber-500/10 ${
                    isSel ? 'bg-zinc-800' : ''
                  }`}
                >
                  <Check className={`h-3.5 w-3.5 flex-shrink-0 ${isSel ? 'text-amber-400' : 'opacity-0'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground truncate">{p.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {p.objective || 'Sin objetivo'}
                      {Array.isArray(p.weeks) ? ` · ${p.weeks.length} semanas` : ''}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PlanSearchSelect;
